import { reaction } from "mobx";
import { some } from "lodash";
import { MrbBaseListViewStore, httpStatusCodes } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { applicationDefaults } from "common/constants";
import { mrbDefaultFormats } from "mrb/common/formatting";
import { localizationService } from "common/localization";

// Batches in these statuses can still be discarded before they're promoted into the system.
const DISCARDABLE_STATUSES = ["Fetched", "Failed"];
// Batches in these statuses are still being processed asynchronously by rss-sync, so the grid polls while any exist.
const ACTIVE_STATUSES = ["Fetching", "Running"];
const POLL_INTERVAL_MS = 4000;

export function isDiscardableStatus(status) {
    return DISCARDABLE_STATUSES.indexOf(status) !== -1;
}

class NakiNigunImportBatchListViewStore extends MrbBaseListViewStore {
    _pollTimeoutId = null;
    _dataReactionDisposer = null;

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            layoutActions: {
                refresh: {
                    onClick: () => this.refresh(),
                    label: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.BUTTONS.REFRESH",
                },
            },
            queryConfig: {
                orderBy: "startedAt",
                orderDirection: "desc",
            },
        });
        this.routeStore = routeStore;

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                onRowClick: (node) =>
                    this.rootStore.routerStore.goTo("master.administration.naki-nigun-import.batch.preview", {
                        id: node.data.id,
                    }),
                actions: {
                    discard: {
                        onClick: (item) => this.discardBatch(item),
                        label: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.BUTTON",
                    },
                },
                columns: [
                    {
                        key: "startedAt",
                        title: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.COLUMNS.DATE",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.date,
                            format: applicationDefaults.formats.dateTime,
                        },
                    },
                    {
                        key: "status",
                        title: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.COLUMNS.STATUS",
                        sortable: true,
                        // Map the raw status enum ("InSystem", "Fetched", …) to a friendly, admin-facing label
                        // using the same keys the detail page uses. Unknown values fall back to the raw string.
                        format: (value) =>
                            value
                                ? localizationService.t(
                                      `ADMINISTRATION.NAKI_NIGUN_IMPORT.PREVIEW.STATUS.${value.toUpperCase()}`
                                  )
                                : value,
                    },
                    {
                        key: "artistsCreated",
                        title: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.COLUMNS.ARTISTS_CREATED",
                        sortable: true,
                    },
                    {
                        key: "artistsFailed",
                        title: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.COLUMNS.ARTISTS_FAILED",
                        sortable: true,
                    },
                    {
                        key: "enabledAt",
                        title: "ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.COLUMNS.ENABLED",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.date,
                            format: applicationDefaults.formats.dateTime,
                        },
                    },
                ],
            })
        );

        // While any batch on the grid is still being fetched/synced, keep re-fetching until it settles.
        this._dataReactionDisposer = reaction(
            () => this.gridStore.data,
            (data) => this._scheduleNextPollIfNeeded(data)
        );
    }

    onDestroy() {
        this._clearPollTimeout();
        if (this._dataReactionDisposer) {
            this._dataReactionDisposer();
        }
        super.onDestroy();
    }

    find(params) {
        return this.routeStore.findBatches(params);
    }

    onFetchError() {
        this.rootStore.notificationStore.error(
            localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.LOAD_ERROR")
        );
    }

    _clearPollTimeout() {
        if (this._pollTimeoutId != null) {
            clearTimeout(this._pollTimeoutId);
            this._pollTimeoutId = null;
        }
    }

    _scheduleNextPollIfNeeded(data) {
        this._clearPollTimeout();
        if (some(data, (batch) => ACTIVE_STATUSES.indexOf(batch.status) !== -1)) {
            this._pollTimeoutId = setTimeout(() => {
                this.queryUtility.fetch();
            }, POLL_INTERVAL_MS);
        }
    }

    refresh = async () => {
        try {
            await this.routeStore.refresh();
            this.queryUtility.fetch();
        } catch (err) {
            if (err && err.statusCode === httpStatusCodes.Conflict) {
                this.rootStore.notificationStore.info(
                    localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.REFRESH.IN_PROGRESS")
                );
            } else {
                this.rootStore.notificationStore.error(
                    localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.REFRESH.ERROR"),
                    err
                );
            }
        }
    };

    discardBatch = (item) => {
        if (!isDiscardableStatus(item.status)) {
            return;
        }
        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.MESSAGE"),
            description: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.DESCRIPTION"),
            yesLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.YES"),
            noLabel: localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.CONFIRM.NO"),
            onConfirm: async () => {
                try {
                    await this.routeStore.discardBatch(item.id);
                    this.rootStore.notificationStore.success(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.SUCCESS")
                    );
                    this.queryUtility.fetch();
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        localizationService.t("ADMINISTRATION.NAKI_NIGUN_IMPORT.LIST.DISCARD.ERROR"),
                        err
                    );
                }
            },
        });
    };
}

export default NakiNigunImportBatchListViewStore;
